import type { InjectionKey, Ref } from 'vue'
import type { CommissioningPollingPhase } from './composables/useCommissioningPollingTimer'
import type { InstallationErrorStatus } from './composables/useInstallationAlerts'
import type { InstallationAnalysisErrorStatus } from './composables/useInstallationAnalysis'
import type { ResolvedCommissioningFlow } from './resolveCommissioningFlow'
import type { CommissioningStatusPayload } from '~/services-v2/financial-bff/commissioning-status.gql'
import { computed, inject, provide, watch } from 'vue'
import useCommissioningStatus from '~/store-v2/installation/useCommissioningStatus'
import useInstallation from '~/store-v2/installation/useInstallation'
import {
  clearCommissioningOptimisticHold,
  holdUntilStatusChanges,
  useCommissioningOptimisticHold,
} from './composables/useCommissioningOptimisticHold'
import useCommissioningPollingTimer from './composables/useCommissioningPollingTimer'
import { resolveCommissioningFlow } from './resolveCommissioningFlow'
import useInstallationFlow, { steps } from './useInstallationFlow'

export interface InstallationCommissioningFlowContext {
  phase: Ref<CommissioningPollingPhase>
  resolved: Ref<ResolvedCommissioningFlow | null>
  labelsAlert: Ref<InstallationErrorStatus | null>
  credentialsAlert: Ref<InstallationErrorStatus | null>
  analysisStage: Ref<number>
  analysisError: Ref<InstallationAnalysisErrorStatus | null>
  commissioningPayload: Ref<CommissioningStatusPayload | undefined>
  holdUntilStatusChanges: (status: string) => void
}

export const INSTALLATION_COMMISSIONING_FLOW_KEY: InjectionKey<InstallationCommissioningFlowContext>
  = Symbol('installationCommissioningFlow')

export function useInstallationCommissioningFlowInject(): InstallationCommissioningFlowContext | undefined {
  return inject(INSTALLATION_COMMISSIONING_FLOW_KEY)
}

function resolveNavigationTarget(
  flow: ResolvedCommissioningFlow,
  payload: CommissioningStatusPayload | undefined,
  heldStatus: string | null,
): { step: typeof steps[keyof typeof steps], context?: string } {
  const installationStatus = payload?.data?.installation_status

  if (heldStatus && installationStatus === heldStatus) {
    return { step: steps.POLLING }
  }

  if (heldStatus && installationStatus && installationStatus !== heldStatus) {
    clearCommissioningOptimisticHold()
  }

  return { step: flow.step, context: flow.stepContext }
}

export default function useInstallationCommissioningFlow(projectId: Ref<string>) {
  const { data: installation } = useInstallation(projectId)
  const installationId = computed(() => installation.value?.id)

  const pollingEnabled = computed(() => Boolean(installationId.value))

  const { data: commissioningPayload } = useCommissioningStatus(installationId, {
    enabled: pollingEnabled,
  })

  const { stepState, handleStepChange } = useInstallationFlow(projectId)
  const { heldUntilStatusChanges } = useCommissioningOptimisticHold()

  const {
    phase,
    start: startPollingTimer,
    reset: resetPollingTimer,
  } = useCommissioningPollingTimer()

  const resolved = computed(() => resolveCommissioningFlow(commissioningPayload.value))

  const labelsAlert = computed(() => resolved.value?.labelsAlert ?? null)
  const credentialsAlert = computed(() => resolved.value?.credentialsAlert ?? null)
  const analysisStage = computed(() => resolved.value?.analysisStage ?? 1)
  const analysisError = computed(() => resolved.value?.analysisError ?? null)

  watch(
    () => ({
      flow: resolved.value,
      payload: commissioningPayload.value,
      held: heldUntilStatusChanges.value,
    }),
    ({ flow, payload, held }) => {
      if (!flow)
        return

      const target = resolveNavigationTarget(flow, payload, held)
      const current = stepState.value

      if (current?.current === target.step && current?.context === target.context)
        return

      if (
        current?.current === target.step
        && current?.stepContextDismissed
        && target.context !== undefined
        && current?.context === undefined
      ) {
        return
      }

      handleStepChange(target.step, target.context)
    },
    { immediate: true },
  )

  watch(() => stepState.value?.current, (current) => {
    if (current === steps.POLLING) {
      resetPollingTimer()
      startPollingTimer()
    }
    else {
      resetPollingTimer()
    }
  }, { immediate: true })

  const context: InstallationCommissioningFlowContext = {
    phase,
    resolved,
    labelsAlert,
    credentialsAlert,
    analysisStage,
    analysisError,
    commissioningPayload: computed(() => commissioningPayload.value),
    holdUntilStatusChanges,
  }

  provide(INSTALLATION_COMMISSIONING_FLOW_KEY, context)

  return context
}
